// src/pages/WelcomePage.tsx
//
// Landing page shown before the login screen. Self-contained: its own CSS
// file, no shared class names with App.css, so it cannot collide with or
// break the existing chat UI. Drop this file in src/pages/ alongside
// LoginPage.tsx / RegisterPage.tsx.
//
// Wiring it in (App.tsx) — three small additions, nothing else touched:
//
//   import WelcomePage from './pages/WelcomePage';
//   const [authStage, setAuthStage] = useState<'welcome' | 'auth'>('welcome');
//
//   if (!user) {
//     if (authStage === 'welcome') {
//       return <WelcomePage onGetStarted={() => setAuthStage('auth')} />;
//     }
//     return authPage === 'login'
//       ? <LoginPage onLogin={login} onGoRegister={() => setAuthPage('register')} />
//       : <RegisterPage onRegister={register} onGoLogin={() => setAuthPage('login')} />;
//   }
//
// Photo: save the uploaded headshot as src/assets/raimal-photo.jpg and the
// import below will pick it up automatically.

import React from 'react';
import raimalPhoto from '../assets/raimal-photo.jpg';
import './WelcomePage.css';

interface WelcomePageProps {
  onGetStarted: () => void;
}

const COURSES = [
  {
    code: 'PROF-101',
    title: 'Full Projects, Not Fragments',
    body: 'Every file a task needs, written in full and in one sitting — no "rest of the code here" placeholders, no stopping halfway through a build.',
  },
  {
    code: 'PROF-102',
    title: 'Live Research',
    body: 'A standing web-search mode for anything time-sensitive, with sources cited by number so you can always check the primary source yourself.',
  },
  {
    code: 'PROF-103',
    title: 'Visual Aids',
    body: 'Ask for a flowchart, a bar chart, or a logo and get back a real diagram, native chart, or SVG — not a paragraph describing one.',
  },
  {
    code: 'PROF-104',
    title: 'Take-Home Materials',
    body: 'Turn a conversation into a PDF report, a Word document, an Excel workbook, or a slide deck — generated on request, ready to download.',
  },
  {
    code: 'PROF-105',
    title: 'Reading List',
    body: 'Save a PDF, DOCX, or image to a personal knowledge base once, and every future conversation can draw on it — nothing re-explained twice.',
  },
  {
    code: 'PROF-106',
    title: 'Field Work',
    body: 'The same model ships as a VS Code extension, CoderAI Auto Bug Fixer, that reads a broken file and proposes the fix inline in your editor.',
  },
];

export default function WelcomePage({ onGetStarted }: WelcomePageProps) {
  return (
    <div className="wp-page">
      <div className="wp-noise" aria-hidden="true" />

      {/* ── Header ─────────────────────────────────────────────────── */}
      <header className="wp-topbar">
        <span className="wp-topbar-mark">✦ PROFESSOR</span>
        <span className="wp-topbar-eyebrow">A PERSONAL AI, IN SESSION SINCE 2025</span>
      </header>

      {/* ── Hero ───────────────────────────────────────────────────── */}
      <section className="wp-hero">
        <div className="wp-hero-copy">
          <p className="wp-eyebrow">Free · Ad-free · One developer</p>
          <h1 className="wp-headline">
            Meet Professor.<br />
            An AI that keeps <em>office hours</em>.
          </h1>
          <p className="wp-sub">
            Professor-Raimal is a custom AI assistant, tuned and run end-to-end
            by a single developer, for coding help and text work that doesn't
            get cut off halfway through the answer.
          </p>
          <div className="wp-hero-actions">
            <button className="wp-cta" onClick={onGetStarted}>
              Get Started <span aria-hidden="true">→</span>
            </button>
            <span className="wp-hero-note">No credit card. No waitlist.</span>
          </div>
        </div>

        <div className="wp-card-wrap">
          <div className="wp-card" role="img" aria-label="Library catalog card for Professor-Raimal">
            <div className="wp-card-row wp-card-row-top">
              <span>SUBJECT</span>
              <span>CATALOG NO.</span>
            </div>
            <div className="wp-card-title">Code &amp; Text Generation</div>
            <div className="wp-card-serial">PROF—RAIMAL / 2025</div>

            <div className="wp-card-divider" />

            <dl className="wp-card-fields">
              <div>
                <dt>Model</dt>
                <dd>Professor-Raimal</dd>
              </div>
              <div>
                <dt>Instructor</dt>
                <dd>Raimal Raja Kolhi</dd>
              </div>
              <div>
                <dt>Location</dt>
                <dd>Sindh, Pakistan</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>In session</dd>
              </div>
            </dl>

            <div className="wp-card-stamp">ENROLLING NOW</div>
          </div>
        </div>
      </section>

      {/* ── Course listing / features ─────────────────────────────── */}
      <section className="wp-syllabus">
        <div className="wp-section-head">
          <h2>The Syllabus</h2>
          <p>Six things Professor actually does, not six things it might do.</p>
        </div>
        <div className="wp-course-grid">
          {COURSES.map(c => (
            <div className="wp-course" key={c.code}>
              <span className="wp-course-code">{c.code}</span>
              <h3>{c.title}</h3>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Faculty / creator ──────────────────────────────────────── */}
      <section className="wp-faculty">
        <div className="wp-section-head">
          <h2>Faculty Record</h2>
          <p>Every model has an author. Here's Professor's.</p>
        </div>

        <div className="wp-faculty-card">
          <img className="wp-faculty-photo" src={raimalPhoto} alt="Raimal Raja Kolhi" />
          <div className="wp-faculty-body">
            <h3>Raimal Raja Kolhi</h3>
            <p className="wp-faculty-role">
              Final-year BS Computer Science, University of Sindh (Laar Campus, Badin)<br />
              ML Engineer &amp; Data Scientist — SignDevOps · Kevi-Tech
            </p>
            <p className="wp-faculty-bio">
              Raimal builds and ships AI tools on his own, end to end — from
              fine-tuning the model behind Professor, to the streaming chat
              proxy it runs on, to a VS Code extension that uses the same
              model to fix bugs inline.
            </p>

            <div className="wp-credentials">
              <span className="wp-cred-badge">🎓 4× Oracle Certified</span>
              <a
                className="wp-cred-link"
                href="https://github.com/Raimal-Raja"
                target="_blank"
                rel="noreferrer"
              >
                Verify credentials on GitHub →
              </a>
              <p className="wp-cred-note"> +923180130173</p>
            </div>

            <div className="wp-links">
              <a href="https://github.com/Raimal-Raja" target="_blank" rel="noreferrer">GitHub</a>
              <a href="https://www.linkedin.com/in/raimal-raja-kolhi-860a032bb/" target="_blank" rel="noreferrer">LinkedIn</a>
              <a href="https://www.instagram.com/professor_raimal/" target="_blank" rel="noreferrer">Instagram</a>
              <a href="https://wa.me/923180130173" target="_blank" rel="noreferrer">WhatsApp</a>
              <a href="https://marketplace.visualstudio.com/items?itemName=RaimalRaja.coderai-auto-bug-fixer" target="_blank" rel="noreferrer">VS Code Extension</a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Enrollment footer ──────────────────────────────────────── */}
      <footer className="wp-enroll">
        <div className="wp-enroll-inner">
          <div>
            <h2>Enrollment is open.</h2>
            <p>Sign in and start your first session with Professor.</p>
          </div>
          <button className="wp-cta wp-cta-lg" onClick={onGetStarted}>
            Get Started <span aria-hidden="true">→</span>
          </button>
        </div>
        <p className="wp-enroll-fine">Professor-Raimal · Built and maintained by Raimal Raja Kolhi</p>
      </footer>
    </div>
  );
}
